import { create } from 'zustand';

export type MultiSelectStore = {
  activePartId: string | null;
  selectedBlockIds: ReadonlySet<string>;
  toggleBlockSelection: (partId: string, blockId: string) => void;
  selectAllInPart: (partId: string, blockIds: string[]) => void;
  clearSelection: () => void;
  isBlockSelected: (partId: string, blockId: string) => boolean;
  isMultiSelectActiveInPart: (partId: string) => boolean;
};

const EMPTY_SET: ReadonlySet<string> = new Set();

export const useMultiSelectStore = create<MultiSelectStore>((set, get) => ({
  activePartId: null,
  selectedBlockIds: EMPTY_SET,

  toggleBlockSelection: (partId, blockId) => {
    const { activePartId, selectedBlockIds } = get();

    if (activePartId && activePartId !== partId) {
      set({ activePartId: partId, selectedBlockIds: new Set([blockId]) });
      return;
    }

    const next = new Set(selectedBlockIds);
    if (next.has(blockId)) {
      next.delete(blockId);
    } else {
      next.add(blockId);
    }

    if (next.size === 0) {
      set({ activePartId: null, selectedBlockIds: EMPTY_SET });
      return;
    }

    set({ activePartId: partId, selectedBlockIds: next });
  },

  selectAllInPart: (partId, blockIds) => {
    if (blockIds.length === 0) {
      set({ activePartId: null, selectedBlockIds: EMPTY_SET });
      return;
    }
    set({ activePartId: partId, selectedBlockIds: new Set(blockIds) });
  },

  clearSelection: () => {
    const { activePartId, selectedBlockIds } = get();
    if (activePartId === null && selectedBlockIds.size === 0) {
      return;
    }
    set({ activePartId: null, selectedBlockIds: EMPTY_SET });
  },

  isBlockSelected: (partId, blockId) => {
    const { activePartId, selectedBlockIds } = get();
    if (activePartId !== partId) return false;
    return selectedBlockIds.has(blockId);
  },

  isMultiSelectActiveInPart: (partId) => {
    const { activePartId, selectedBlockIds } = get();
    return activePartId === partId && selectedBlockIds.size > 0;
  },
}));

export const selectIsAnyMultiSelectActive = (state: MultiSelectStore) =>
  state.activePartId !== null && state.selectedBlockIds.size > 0;
